// schemas.ts
import { differenceInYears } from 'date-fns/differenceInYears'
import * as z from 'zod'

import { EUserRole } from '@/hooks/usePermissions/permissions.type'

import { sanitizeSocialMediaUrl } from './sanitizeSocialMediaUrl'

// Regex:
const phoneRegex = /^(.*)$/

const emailRegex = /^(\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+)?$/

const nameRegex = /^(?!.*([áéíóúÁÉÍÓÚñÑ'’`])\1{2,})([a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-])+$/

const middleNameRegex =
  /^(?!.*([áéíóúÁÉÍÓÚñÑ'’`])\1{2,})([a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]*)$/

const alphanumericWithSpaces = /^([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'-])*$/

const alphanumericWithSpacesAndPoints =
  /^(?!.*([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ])\1{2,})([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s'-]*(\.[^.\s]*){0,2})*$/

const addressRegex =
  /^(?!.*([áéíóúÁÉÍÓÚüÜñÑ])\1{2,})([\w\d\s.,\-áéíóúÁÉÍÓÚüÜñÑ]+)$/

const passwordRegex =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&_*-]).{8,}$/

const cityRegex =
  /^(?!.*([a-zA-ZáéíóúÁÉÍÓÚñÑ])\1{2,})([a-zA-ZáéíóúÁÉÍÓÚñÑ\s.']*)$/
const state = /^(?!.*([a-zA-ZáéíóúÁÉÍÓÚñÑ])\1{2,})([a-zA-ZáéíóúÁÉÍÓÚñÑ ,.'-]*)$/

const numbers = /^(\d{1,6})?$/

const phoneOptional = /^(.*)$/

const organizationName =
  /^(?!.*([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ])\1{2,})([A-Za-z0-9áéíóúÁÉÍÓÚñÑ_.,;:¡!¿?"'@#~$%&*()\-+=\[\]{}<>\s]*)$/

const socialMediaRegex =
  /^(https?:\/\/)?(www\.)?(x\.com|tiktok\.com|linkedin\.com|facebook\.com|instagram\.com|pinterest\.com)?\/?.*$/

const optionalCollegeRegex =
  /^(?!.*([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ])\1{2,})([a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s,'-]{1,})?$/

const websiteRegex =
  /^(https?:\/\/)?([\w\-]+\.)+[\w\-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$|^$/

export const linkedInUrlRegex =
  /^(https?:\/\/)?(www\.)?linkedin\.com\/[a-zA-Z0-9-_/]*$/

export const linkedInCompanyUrlRegex =
  /^(https?:\/\/)?(www\.)?linkedin\.com\/(company|school|organization)\/[a-zA-Z0-9-_/]*$/

// Validation Functions
const webhookURLValidation = z
  .string()
  .trim()
  .url({
    message: 'Must be a valid URL.',
  })
  .refine((value) => !value.includes(' ') && !value.includes('\n'), {
    message: 'URL cannot contain spaces or line breaks',
  })

const booleanValidation = z
  .boolean()
  .refine((val) => val === true || val === false, {
    message: 'feedback.validations.booleanValidation',
  })

const roleValidation = z.enum(
  [
    EUserRole.OWNER,
    EUserRole.CO_OWNER,
    EUserRole.SUPERVISOR,
    EUserRole.WORKSPACE_ADMIN,
    EUserRole.MANAGER,
    EUserRole.TEAM_MANAGER,
    EUserRole.TEAM_ANALYST,
    EUserRole.OPERATIONAL_USER,
    EUserRole.VIEW_USER,
    EUserRole.READER,
  ],
  {
    errorMap: () => ({ message: 'feedback.validations.invalidRole' }),
  },
)

const pdfLanguageValidation = z
  .string()
  .min(1, { message: 'feedback.validations.pdfLanguageValidation' })

const emailValidation = z
  .string()
  .min(1, { message: 'feedback.validations.emailMinValidation' })
  .regex(emailRegex, 'feedback.validations.emailRegexValidation')

const optionalEmailValidation = z
  .string()
  .optional()
  .refine((value) => !value || emailRegex.test(value), {
    message: 'feedback.validations.emailRegexValidation',
  })

const creditsValidation = z.number()

const passwordValidation = z
  .string()
  .min(8, { message: 'feedback.validations.passwordMinValidation' })
  .max(128, {
    message: 'feedback.validations.passwordMaxValidation',
  })
  .regex(passwordRegex, 'feedback.validations.passwordRegexValidation')

// New bcrypt credentials must fit its 72-byte UTF-8 input limit. Keep the
// existing login validator separate so legacy long passwords still sign in.
export const newPasswordValidation = z
  .string()
  .min(8, { message: 'feedback.validations.passwordMinValidation' })
  .regex(passwordRegex, 'feedback.validations.passwordRegexValidation')
  .refine((value) => new TextEncoder().encode(value).byteLength <= 72, {
    message: 'security.passwordTooLong',
  })

const nameValidation = z
  .string()
  .min(2, { message: 'feedback.validations.nameMinValidation' })
  .regex(nameRegex, 'feedback.validations.nameRegexValidation')

const phoneValidation = z
  .string()
  .min(1, { message: 'feedback.validations.phoneMinValidation' })
  .regex(phoneRegex, 'feedback.validations.phoneRegexValidation')

const commentValidation = z
  .string()
  .min(4, { message: 'feedback.validations.commentMinValidation' })
  .max(200, {
    message: 'feedback.validations.commentMaxValidation',
  })

const commentOptionalValidation = z
  .string()
  .max(200, {
    message: 'feedback.validations.commentMaxValidation',
  })
  .optional()

const optionalNameValidation = z
  .string()
  .regex(optionalCollegeRegex, 'feedback.validations.nameRegexValidation')
  .optional()

const aliasValidation = z
  .string()
  .max(50, { message: 'feedback.validations.firstAndLastNameMaxValidation' })
  .optional()

const firstAndLastNameValidation = z
  .string()
  .min(2, { message: 'feedback.validations.nameMinValidation' })
  .max(50, { message: 'feedback.validations.firstAndLastNameMaxValidation' })
  .regex(nameRegex, 'feedback.validations.nameRegexValidation')

const middleNameValidation = z
  .string()
  .regex(middleNameRegex, 'feedback.validations.nameRegexValidation')
  .optional()

const companyNameValidation = z
  .string()
  .min(2, { message: 'feedback.validations.companyNameMinValidation' })
  .max(50, { message: 'feedback.validations.companyNameMaxValidation' })
  .regex(alphanumericWithSpaces, 'feedback.validations.nameRegexValidation')

const workspaceNameValidation = z
  .string()
  .min(2, { message: 'feedback.validations.companyNameMinValidation' })
  .max(50, { message: 'feedback.validations.companyNameMaxValidation' })
  .regex(alphanumericWithSpacesAndPoints, {
    message: 'feedback.validations.nameRegexValidation',
  })

const optionalCompanyNameValidation = z
  .string()
  .max(30, { message: 'feedback.validations.companyNameMaxValidation' })
  .regex(alphanumericWithSpaces, 'feedback.validations.nameRegexValidation')
  .optional()

const companyAddressValidation = z
  .string()
  .min(2, { message: 'feedback.validations.companyAddressMinValidation' })
  .max(120, { message: 'feedback.validations.companyAddressMaxValidation' })
  .regex(addressRegex, 'feedback.validations.companyAddressRegexValidation')

const cityValidation = z
  .string()
  .optional()
  .refine((value) => !value || cityRegex.test(value), {
    message: 'feedback.validations.cityRegexValidation',
  })

const countryValidation = z
  .string()
  .min(3, { message: 'feedback.validations.countryMinValidation' })
  .regex(cityRegex, 'feedback.validations.countryRegexValidation')
// Form location inputs only get a value when a Google suggestion is picked, so
// an empty value means "not selected" (and Google names can have any accent).
const selectedCountryValidation = z
  .string()
  .min(1, { message: 'feedback.validations.locationSelectValidation' })
const stateValidation = z
  .string()
  .optional()
  .refine((value) => !value || state.test(value), {
    message: 'feedback.validations.stateRegexValidation',
  })

const pinValidation = z.string().min(6)

const newSearchPhone = z.preprocess(
  (a) => {
    if (a) {
      return a
    } else {
      return ''
    }
  },
  z.string().regex(phoneOptional, {
    message: 'feedback.validations.newSearchPhoneRegexValidation',
  }),
)

const requiredPhone = z
  .string({ description: 'feedback.validations.requiredPhoneValidation' })
  .min(9)

const backgroundChecksNumber = z
  .string()
  .max(6, 'feedback.validations.backgroundChecksNumberMaxValidation')
  .regex(numbers, 'feedback.validations.backgroundChecksNumberRegexValidation')

const organizationNameValidation = z
  .string()
  .regex(
    organizationName,
    'feedback.validations.organizationNameRegexValidation',
  )
  .min(2, 'feedback.validations.organizationNameMinValidation')
  .max(128, 'feedback.validations.organizationNameMaxValidation')
const optionalWebsiteValidation = z
  .string()
  .regex(websiteRegex, 'feedback.validations.optionalEmailwebsiteValidation')
  .optional()

// Schemas
export const loginSchema = z.object({
  email: emailValidation,
  password: passwordValidation,
})

export const contactUsSchema = z.object({
  organizationName: organizationNameValidation,
  name: nameValidation,
  email: emailValidation,
  phoneNumber: newSearchPhone,
  comment: commentValidation,
})

export const BackgroundCheckContactUsSchema = z.object({
  comment: commentValidation,
})

export const signUpSchema = z.object({
  email: emailValidation,
  createPassword: newPasswordValidation,
  confirmPassword: newPasswordValidation,
})

export const signUpFirstStepSchema = z.object({
  firstName: firstAndLastNameValidation,
  lastName: firstAndLastNameValidation,
  phoneNumber: phoneValidation,
})

export const signUpSecondStepSchema = z.object({
  companyName: organizationNameValidation,
  companyAddress: companyAddressValidation,
})

export const signUpFinalStepSchema = z.object({
  backgroundChecksNumber: backgroundChecksNumber,
  comment: commentOptionalValidation,
})

export const socialUrlSchema = z
  .string()
  .max(150, 'feedback.validations.socialUrlSchemaMaxValidation')
  .regex(
    socialMediaRegex,
    'feedback.validations.socialUrlSchemaRegexValidation',
  )

const isValidDateRange = (date: string, canPassMinor: boolean) => {
  if (!date || date === '' || (date && date.length < 10)) {
    return true
  }

  const years = differenceInYears(new Date(), new Date(date))

  if (canPassMinor) {
    return years >= 1 && years <= 130
  } else {
    return years >= 18 && years <= 130
  }
}

const isValidDate = (date: string) => {
  if (
    !date ||
    date === '' ||
    (date && date.length < 2) ||
    (date && date.length > 2 && date.length < 10)
  ) {
    return false
  }
  return true
}

const optionalIsValidDate = (date: string | undefined | null) => {
  if (!date) {
    return true
  }
  if (date.length < 2 || (date.length > 2 && date.length < 10)) {
    return false
  }
  return true
}

const isValidNumericAge = (date: string, canPassMinor: boolean) => {
  if (
    !date ||
    date === '' ||
    (date && date.length < 2) ||
    (date && date.length > 2 && date.length <= 10)
  ) {
    return true
  }
  const castedAge = Number(date)
  if (canPassMinor) {
    return castedAge > 9
  } else {
    return castedAge > 17
  }
}

export const dob = z
  .string()
  .refine((data) => isValidDateRange(data, false), {
    message: 'feedback.validations.dobIsValidDateRange',
  })
  .refine((data) => isValidDate(data), {
    message: 'feedback.validations.dobIsValidDate',
  })
  .refine((data) => isValidNumericAge(data, false), {
    message: 'feedback.validations.dobIsValidNumericAge',
  })

export const optionalDob = z
  .string()
  .refine((data) => isValidDateRange(data, true), {
    message: 'feedback.validations.dobWithMinorIsValidDateRange',
  })
  .refine((data) => optionalIsValidDate(data), {
    message: 'feedback.validations.dobWithMinorIsValidDate',
  })
  .refine((data) => isValidNumericAge(data, true), {
    message: 'feedback.validations.dobWithMinorIsValidNumericAge',
  })
  .optional()

export const dobWithMinor = z
  .string()
  .refine((data) => isValidDateRange(data, true), {
    message: 'feedback.validations.dobWithMinorIsValidDateRange',
  })
  .refine((data) => isValidDate(data), {
    message: 'feedback.validations.dobWithMinorIsValidDate',
  })
  .refine((data) => isValidNumericAge(data, true), {
    message: 'feedback.validations.dobWithMinorIsValidNumericAge',
  })

export const loadSocialMediaUrlSchema = z.object({
  socialMediaUrl: z.string().transform((url) => sanitizeSocialMediaUrl(url)),
})

export const profileSchema = z.object({
  firstName: nameValidation,
  lastName: nameValidation,
  phoneNumber: requiredPhone.optional().or(z.literal('')),
  mail: optionalEmailValidation,
})

export const enterpriseSchema = z.object({
  companyName: companyNameValidation,
  companyAddress: companyAddressValidation.optional().or(z.literal('')),
})

export const forgotPasswordSchema = z.object({
  email: emailValidation,
})

export const invitePasswordFlowSchema = z.object({
  createPassword: newPasswordValidation,
  confirmPassword: newPasswordValidation,
})

export const inviteNameFlowSchema = z.object({
  firstname: nameValidation,
  lastname: nameValidation,
})

export const createPasswordSchema = z.object({
  createPassword: newPasswordValidation,
  confirmPassword: newPasswordValidation,
})

export const emailValidationSchema = z.object({
  pin: pinValidation,
})

export const pDFReportSectionSchema = z.object({
  coreFindings: booleanValidation,
  flaggedBehaviors: booleanValidation,
  language: pdfLanguageValidation,
  negativePosts: booleanValidation,
  neutralPosts: booleanValidation,
  positivePosts: booleanValidation,
  sentimentAnalysis: booleanValidation,
  socialMediaAccounts: booleanValidation,
  webNewsPosts: booleanValidation,
  personalityAndSkills: booleanValidation,
  educationAndCareer: booleanValidation,
  digitalFootprint: booleanValidation,
  contentRadar: booleanValidation,
  unverifiedDigitalFootprint: booleanValidation,
})

export const workspaceSchema = z.object({
  workspaceName: workspaceNameValidation,
})

export const workspaceSecondStepSchema = z.object({
  createTeamName: workspaceNameValidation,
})

export const workspaceThirdStepSchema = z.object({
  email: emailValidation,
})

export const createTeamSecondStepSchema = z.object({
  email_0: emailValidation,
  email_1: emailValidation.optional(),
  email_2: emailValidation.optional(),
  email_3: emailValidation.optional(),
  email_4: emailValidation.optional(),
})

export const creditsSchema = z.object({
  ai_report: creditsValidation,
  ai_analyst_report: creditsValidation,
  adverse_media_report: creditsValidation,
  assisted_report: creditsValidation,
  facial_matching: creditsValidation,
})

export const apiKeySchema = z.object({
  name: companyNameValidation,
  webhookUrl: webhookURLValidation,
})

export const renameSchema = z.object({
  name: companyNameValidation,
})

export const inviteWorkspaceSchema = z.object({
  email: emailValidation,
})

export const inviteMemberValidation = z.object({
  email: emailValidation,
  role: roleValidation,
})

export const manageTeamsFormSchema = z.object({
  teams: z.record(z.array(z.string())),
  role: z.string().optional(),
})

export type LoginType = z.TypeOf<typeof loginSchema>
export type ContactUsType = z.TypeOf<typeof contactUsSchema>
export type BackgroundCheckContactUsType = z.TypeOf<
  typeof BackgroundCheckContactUsSchema
>
export type LoadSocialMediaUrlSchemaType = z.TypeOf<
  typeof loadSocialMediaUrlSchema
>
export type signUpType = z.TypeOf<typeof signUpSchema>
export type signUpFirstStepType = z.TypeOf<typeof signUpFirstStepSchema>
export type signUpSecondStepType = z.TypeOf<typeof signUpSecondStepSchema>
export type signUpFinalStepType = z.TypeOf<typeof signUpFinalStepSchema>
export type forgotPasswordType = z.TypeOf<typeof forgotPasswordSchema>
export type invitePasswordFlowType = z.TypeOf<typeof invitePasswordFlowSchema>
export type inviteNameFlowType = z.TypeOf<typeof inviteNameFlowSchema>
export type createPasswordType = z.TypeOf<typeof createPasswordSchema>
export type emailValidationType = z.TypeOf<typeof emailValidationSchema>
export type profileType = z.TypeOf<typeof profileSchema>
export type enterpriseType = z.TypeOf<typeof enterpriseSchema>

export type pDFReportSectionType = z.TypeOf<typeof pDFReportSectionSchema>
export type workspaceSchemaType = z.TypeOf<typeof workspaceSchema>
export type creditsSchemaType = z.TypeOf<typeof creditsSchema>
export type apiKeySchemaType = z.TypeOf<typeof apiKeySchema>
export type renameSchemaType = z.TypeOf<typeof renameSchema>
export type inviteWorkspaceSchemaType = z.TypeOf<typeof inviteWorkspaceSchema>
export type workspaceSecondStepSchemaType = z.TypeOf<
  typeof workspaceSecondStepSchema
>
export type workspaceThirdStepSchemaType = z.TypeOf<
  typeof workspaceThirdStepSchema
>
export type createTeamSecondStepSchemaType = z.TypeOf<
  typeof createTeamSecondStepSchema
>
export type inviteMemberValidationType = z.TypeOf<typeof inviteMemberValidation>
export type ManageTeamsFormValues = z.infer<typeof manageTeamsFormSchema>

export {
  emailValidation,
  firstAndLastNameValidation,
  middleNameValidation,
  optionalNameValidation,
  countryValidation,
  stateValidation,
  cityValidation,
  newSearchPhone,
}

// Age validation for batch processing - accepts both strings and numbers
export const ageValidation = z
  .union([z.string(), z.number()])
  .optional()
  .refine(
    (value) => {
      // If empty/null/undefined, it's valid (optional field)
      if (value === null || value === undefined || value === '') return true

      // Convert to number for validation
      const num =
        typeof value === 'number' ? value : Number(String(value).trim())
      if (isNaN(num)) return false

      // Check if it's a reasonable age (1-150)
      return num >= 1 && num <= 150
    },
    {
      message: 'Age must be a number between 1 and 150',
    },
  )

const socialMediaPatterns = {
  facebook:
    /^(https?:\/\/)?(www\.)?(facebook\.com\/(profile\.php\?id=\d+|[A-Za-z0-9._-]+|p\/[A-Za-z0-9._-]+))$/,
  x: /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/[A-Za-z0-9._-]+$/,
  instagram: /^(https?:\/\/)?(www\.)?instagram\.com\/[A-Za-z0-9._-]+$/,
  linkedIn:
    /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company)\/[A-Za-z0-9._-]+\/?$/,
  tikTok: /^(https?:\/\/)?(www\.)?tiktok\.com\/@[A-Za-z0-9._-]+$/,
  pinterest: /^(https?:\/\/)?(www\.)?pinterest\.com\/[A-Za-z0-9._-]+$/,
  reddit: /^(https?:\/\/)?(www\.)?reddit\.com\/user\/[A-Za-z0-9._-]+\/?$/,
}

const optionalSocialUrl = (
  pattern: RegExp,
  errorKey = 'feedback.validations.invalidSocialMediaUrl',
) =>
  z
    .string()
    .nullish()
    .transform((url) => sanitizeSocialMediaUrl(url ?? ''))
    .refine((url) => url === '' || pattern.test(url), { message: errorKey })

export const socialMediaSheetSchema = z.object({
  facebook: optionalSocialUrl(socialMediaPatterns.facebook),
  instagram: optionalSocialUrl(socialMediaPatterns.instagram),
  x: optionalSocialUrl(socialMediaPatterns.x),
  linkedIn: optionalSocialUrl(socialMediaPatterns.linkedIn),
  tikTok: optionalSocialUrl(socialMediaPatterns.tikTok),
  pinterest: optionalSocialUrl(socialMediaPatterns.pinterest),
  reddit: optionalSocialUrl(socialMediaPatterns.reddit),
})

export type socialMediaSheetSchemaType = z.TypeOf<typeof socialMediaSheetSchema>

const hasAnySocialUrl = (
  socialMedia: socialMediaSheetSchemaType | undefined,
): boolean =>
  !!socialMedia &&
  Object.values(socialMedia).some((url) => url.trim().length > 0)

export const confirmSubjectDetailsSchema = z
  .object({
    image: z.string().nullable(),
    email: z
      .string()
      .regex(emailRegex, 'feedback.validations.optionalEmailRegexValidation'),
    socialMedia: socialMediaSheetSchema,
  })
  .refine(
    (data) =>
      Boolean(data.image) ||
      data.email.trim().length > 0 ||
      hasAnySocialUrl(data.socialMedia),
    {
      message: 'feedback.validations.atLeastOneIdentifierRequired',
      path: ['email'],
    },
  )

export type confirmSubjectDetailsSchemaType = z.TypeOf<
  typeof confirmSubjectDetailsSchema
>

export const refineSearchSchema = z.object({
  image: z.string().nullable(),
  firstName: firstAndLastNameValidation,
  middleName: middleNameValidation,
  lastName: firstAndLastNameValidation,
  alias: aliasValidation,
  dob: optionalDob,
  email: optionalEmailValidation,
  countryLongName: selectedCountryValidation,
  stateLongName: stateValidation,
  cityLongName: cityValidation,
  socialMedia: socialMediaSheetSchema,
})

export type refineSearchSchemaType = z.TypeOf<typeof refineSearchSchema>

export const addExtraDetailsSchema = z.object({
  image: z.string().nullable(),
  employerName: optionalCompanyNameValidation,
  college: optionalNameValidation,
  alias: aliasValidation,
  phoneNumber: z.string().optional(),
  // Free text about the person; the find-module uses it for semantic search + triage.
  description: z.string().trim().max(2000).optional(),
  socialMedia: socialMediaSheetSchema,
})

export type addExtraDetailsSchemaType = z.TypeOf<typeof addExtraDetailsSchema>

export const subjectFormSchema = z.object({
  image: z.string().nullable(),
  firstName: firstAndLastNameValidation,
  middleName: middleNameValidation,
  lastName: firstAndLastNameValidation,
  alias: aliasValidation,
  dob: optionalDob,
  email: optionalEmailValidation,
  countryLongName: selectedCountryValidation,
  stateLongName: stateValidation,
  cityLongName: cityValidation,
  socialMedia: socialMediaSheetSchema,
})

export type subjectFormSchemaType = z.TypeOf<typeof subjectFormSchema>

export const companyFormSchema = z.object({
  companyName: companyNameValidation,
  companyWebsite: optionalWebsiteValidation,
  industry: optionalCompanyNameValidation,
  description: z.string().trim().max(2000).optional(),
  countryLongName: selectedCountryValidation,
  stateLongName: stateValidation,
  cityLongName: cityValidation,
  socialMedia: socialMediaSheetSchema,
})

export type companyFormSchemaType = z.TypeOf<typeof companyFormSchema>

export const submitSubjectInfoEditSchema = z.object({
  firstName: firstAndLastNameValidation,
  middleName: middleNameValidation,
  lastName: firstAndLastNameValidation,
  alias: aliasValidation,
  dob: optionalDob,
  email: emailValidation,
  phoneNumber: z.string().optional(),
  employerName: optionalCompanyNameValidation,
  college: optionalNameValidation,
  countryLongName: selectedCountryValidation,
  stateLongName: stateValidation,
  cityLongName: cityValidation,
})

export type submitSubjectInfoEditSchemaType = z.TypeOf<
  typeof submitSubjectInfoEditSchema
>

export const FOCUSED_TOPICS_MAX = 5

export const behaviorFlagsSchema = z.object({
  topics: z.array(z.string()).max(FOCUSED_TOPICS_MAX, {
    message: 'feedback.validations.focusedTopicsMaxValidation',
  }),
})

export type behaviorFlagsSchemaType = z.TypeOf<typeof behaviorFlagsSchema>

export const customizeReportSchema = z.object({
  pdfLanguage: z.string(),
  pdfCoreFindings: z.boolean(),
  pdfSocialMediaAccounts: z.boolean(),
  pdfSentimentAnalysis: z.boolean(),
  pdfFlaggedBehaviors: z.boolean(),
  pdfPositivePosts: z.boolean(),
  pdfNeutralPosts: z.boolean(),
  pdfNegativePosts: z.boolean(),
  pdfWebSearchPosts: z.boolean(),
  pdfPersonalityAndSkills: z.boolean(),
  pdfEducationAndCareer: z.boolean(),
  pdfDigitalFootprint: z.boolean(),
  pdfContentRadar: z.boolean(),
  pdfUnverifiedDigitalFootprint: z.boolean(),
})

export type customizeReportSchemaType = z.TypeOf<typeof customizeReportSchema>
